import * as pc from 'playcanvas';

import type { WalkConfig } from '../scene/scene-types';
import {
    buildWalkSurfaces, resolveWalkPoint, seedWalkPoint, raycastWalkSurfaces,
    planeHeightAt, pointInPolygon, CANONICAL_EYE_HEIGHT, STEP_TOLERANCE,
    type WalkSurface
} from '../walk-geometry';
import {
    resolveVoxelWalkPoint, seedVoxelWalkPoint,
    type VoxelCollider, type VoxelWalkPoint
} from '../../../shared/scene-core/voxel-walk';
import { DeviceLook } from './device-look';

// hover target with collision voxels: how far the cursor ray looks for a
// surface, and how far in front of an object (beyond the body radius) a
// click on it stops
const OBJECT_PICK_RANGE = 40;
const OBJECT_STANDOFF = 0.2;
// a surface this close to level (cos 30°) gets the flat floor disc
const FLOOR_NORMAL_Y = 0.85;
/** A voxel hit this close to a walk surface is the floor: the disc lies flat
 *  there, whatever the voxel normal says (patchy voxels at floor level give
 *  tilted normals). */
const FLOOR_SNAP = 0.2;

/**
 * WalkController — first-person walk navigation:
 *   - DRAG-LOOK: press + drag rotates the camera — the same scheme on mouse
 *     and touch. Default is GRAB (Street View / Matterport): you pull the
 *     picture, so dragging right swings the view left. User tests (Oct 2026):
 *     first-time visitors expected that and fought the FPS direction. Hover-look (the cursor's resting
 *     position steering the camera, Spark ExploreB style) is gone: it spun
 *     the view whenever the mouse crossed the page and nobody discovered it.
 *   - HOVER indicator: a disc on the floor shows where a click would take you.
 *   - CLICK/TAP-TO-MOVE: a clean click (little travel, quick release) glides
 *     to that floor point; a real drag is look, and cancels a glide.
 *   - WASD / arrows: velocity-lerped walk. Wheel / two-finger trackpad
 *     scroll: vertical walks forward/back, horizontal steps sideways. The
 *     scroll direction is learned (natural scrolling flips it, and a page
 *     can't read that setting) — see startScrollCalibration().
 *   - PINCH (trackpad ctrl+wheel, or two fingers spreading on glass) walks
 *     toward the point between the fingers. People pinch to "get closer";
 *     narrowing the FOV would just look broken in a walkable room.
 *   - Touch: two fingers sliding = the trackpad scroll; spreading = pinch.
 *
 * The walkable region comes from the scene's `walk` block, flattened into a
 * SURFACE list (ground floor + extra `levels` + `stairs` ramps). Every frame
 * the wished-for XZ resolves against those surfaces (shared walk-geometry):
 * camera height follows the surface under it + eyeHeight, stairs ramp the
 * height between floors, and unreachable surfaces (a storey 3 m up) act as
 * walls even where footprints overlap in XZ. Single-floor scenes reduce to
 * the old clamp-to-boundary behavior exactly.
 *
 * With collision voxels (setVoxels, scene.json `walk.voxels`) the surfaces
 * still give XZ and the height, and the voxels block the body at objects and
 * walls (shared voxel-walk, the same code as the editor's walk preview).
 *
 * Mirrors OrbitController's contract: constructor(camera, opts), attach(canvas),
 * detach(), update(dt), initFromPose(pos, target).
 */

// ── Tuning (verbatim from Spark ExploreB / NavUtils) ───────────────────────
const WALK_SPEED = 2.0;          // units/sec at full velocity
const WALK_ACCEL = 0.12;         // velocity lerp factor toward target
const VEL_DEADBAND = 0.01;       // snap tiny velocity to zero
const PITCH_CLAMP = (60 * Math.PI) / 180;
const Y_LERP = 0.2;              // floor-height follow (smooths tilt / steps)
const RAD2DEG = 180 / Math.PI;
// Hold-to-walk: a finger that has stayed within HOLD_MAX_PX for HOLD_MS is a
// hold, not the start of a swipe; once held it stays held even if it drifts.
const HOLD_MS = 250;
const HOLD_MAX_PX = 12;
// Gyro schemes: a double-tap recenters the view. A single tap's glide waits
// out the window, so the first tap of a double never starts walking.
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 40;
// How fast lining up with the device (scheme start, recenter) closes the gap,
// per second: ~90% in 0.4 s.
const GYRO_ALIGN_RATE = 6;
// Wheel / trackpad dolly, metres of velocity per wheel px. A notched wheel
// sends ~100 px per click; a trackpad sends small px deltas, many per gesture
// plus momentum, so it gets a calmer factor (it used to cross the room).
const WHEEL_NOTCH_SENS = 0.0008;
const WHEEL_TRACKPAD_SENS = 0.00022;
// Velocity cap per frame for the decaying scroll / strafe / pinch impulses.
const IMPULSE_MAX = 0.09;
// Trackpad pinch arrives as ctrl+wheel with small deltaY (negative = spread).
const PINCH_WHEEL_SENS = 0.004;
// Touch: metres of impulse per px of finger travel.
const TOUCH_PAN_SENS = 0.0022;
const TOUCH_PINCH_SENS = 0.0045;
// Two fingers on glass: decide pan vs pinch after this much travel, then lock.
const TWO_FINGER_DECIDE_PX = 12;
// Click on a wall / painting: walk toward it and stop this far short.
const WALL_STOP_M = 0.9;
const WALL_MARCH_STEP = 0.1;
const WALL_MARCH_MAX = 25;
// Scroll direction learned in the tour (1 = classic, -1 = natural).
const SCROLL_SIGN_KEY = 'olloport.scrollSign';
// Click-target ring: Olloport terracotta #c87048.
const RING_COLOR = new pc.Color(200 / 255, 112 / 255, 72 / 255);

const smoothstep = (t: number): number => {
    const c = t < 0 ? 0 : t > 1 ? 1 : t;
    return c * c * (3 - 2 * c);
};

export interface WalkOptions {
    app: pc.AppBase;
    walk: WalkConfig;
}

/**
 * Hover-to-look tuning (scheme C). Ported from the legacy viewer's ExploreB:
 * the cursor resting inside a screen margin turns the view toward that edge,
 * faster the deeper it sits; a cursor merely passing through on its way to a
 * button is ignored.
 */
export interface EdgeLookConfig {
    /** Fraction of the canvas width/height that is margin on EACH side. */
    margin: number;
    /** Peak turn rate at the very edge, rad/s. */
    maxYaw: number;
    /** Same for the top/bottom margins. 0 = yaw only. */
    maxPitch: number;
    /** Response curve exponent: 1 linear, 3 = a drift until deep in. */
    ease: number;
    /** DC offset under the curve, as a fraction of the peak: the rate the
     *  moment the cursor crosses into a margin, added everywhere in it (so
     *  the screen edge runs at (1 + floor) x peak). 0 = curve from nothing. */
    floor: number;
    /** Cursor speed (px/s) at which turning is fully suppressed — a flick
     *  toward the toolbar must not whip the view on its way past. */
    suppressSpeed: number;
    /** Top-right rectangle (CSS px) where hovering never turns: the toolbar
     *  and the cog live there, and aiming at them is not looking. */
    uiDeadZone: { w: number; h: number };
}

/** Pointer-lock mouse-look tuning (scheme E): a click captures the mouse,
 *  raw motion turns the view, Esc (the browser's own exit) releases it. */
export interface PointerLookConfig {
    /** rad per px of raw mouse motion. */
    sens: number;
}

/** Device-orientation look (mobile schemes): turning the phone/tablet turns
 *  the view. Gains of 1 = one-to-one with the device. */
export interface GyroLookConfig {
    yawGain: number;
    pitchGain: number;
}

/** Two-finger dolly (mobile scheme D): two fingers sliding up/down move
 *  forward/back — the touch counterpart of the wheel. Units per finger px. */
export interface TwoFingerMoveConfig {
    sens: number;
}

/** Where the joystick lives: the shipped fixed pad bottom-right, or a pad
 *  that appears wherever a finger lands on the left half of the screen. */
export type JoystickMode = 'fixed' | 'floating';

/**
 * A mouse or trackpad is present (a computer, a touch-screen laptop, an iPad
 * with a trackpad): never an on-screen joystick there, whatever a scheme or
 * caller asks for (Ilias, 04/11/2026). Phones and bare tablets have no fine
 * pointer and keep it.
 */
function hasFinePointer(): boolean {
    try { return typeof matchMedia === 'function' && matchMedia('(any-pointer: fine)').matches; } catch { return false; }
}

export class WalkController {
    private readonly camera: pc.Entity;
    private readonly app: pc.AppBase;
    private readonly surfaces: WalkSurface[];
    private readonly eyeHeight: number;

    // Resolved floor height under the camera (feet, not eye). NaN → re-seed on
    // the next update using seedFeetY as the preferred floor.
    private feetY = NaN;

    private seedFeetY = NaN;

    // Voxel collision, once loaded (null = plane-only walking).
    private voxels: VoxelCollider | null = null;

    private voxelPoint: VoxelWalkPoint | null = null;

    private yaw = 0;          // radians
    private pitch = 0;        // radians

    private velF = 0;
    private velR = 0;
    private scrollVel = 0;       // wheel dolly velocity along the view direction
    private strafeVel = 0;       // sideways trackpad / two-finger velocity
    private pinchVX = 0;         // pinch impulse, world XZ, toward the pinch point
    private pinchVZ = 0;
    /** Drag pulls the picture (grab, default) or turns the head (FPS). */
    private grabLook = true;
    /** Wheel delta → finger direction: 1 classic, -1 natural scrolling.
     *  Guessed from the platform until the tour learns the real one. */
    private scrollSign = 1;
    private scrollSignKnown = false;
    private calibrating = false;
    private calibAccum = 0;
    /** Touch two-finger navigation (pan = walk/strafe, spread = get closer). */
    private twoFingerNav = true;
    private tfMode: 'undecided' | 'pan' | 'pinch' = 'undecided';
    private tfStartCX = 0; private tfStartCY = 0; private tfStartDist = 0;
    private tfPrevCX = 0; private tfPrevCY = 0; private tfPrevDist = 0;
    private readonly keys = new Set<string>();

    // Hover cursor position — drives the floor-target indicator only. The
    // cursor's resting position never rotates the camera.
    private mouseX = 0;
    private mouseY = 0;
    private mouseIn = false;

    // Virtual joystick (mouse + touch) → movement vector, like Spark's nipplejs.
    private joystickX = 0;      // +right
    private joystickY = 0;      // +forward
    private joyEl: HTMLElement | null = null;
    private joyKnob: HTMLElement | null = null;
    private joyPointerId: number | null = null;
    private readonly joyMaxTravel = 32;  // px of knob travel = full deflection

    // ── Scheme-configurable capabilities ─────────────────────────────────
    // Every default here IS the shipped behaviour, so a controller nobody
    // reconfigures behaves exactly as it always has. The A/B/C/D experiment
    // flips them through the setters below; nothing else does.
    private joystickAllowed = true;
    private clickToMove = true;
    /** WASD speed multiplier while Shift is held. 1 = no boost (shipped). */
    private shiftBoost = 1;
    private shiftDown = false;
    /** Scales how far each wheel tick dollies. 1 = the shipped feel. */
    private wheelSensitivity = 1;
    /** Press-and-drag rotates the view. Off (scheme C), a press turns nothing. */
    private dragLook = true;
    /** Hover-to-look: the cursor near a screen edge turns the view. null =
     *  off, the shipped behaviour. See EdgeLookConfig. */
    private edgeLook: EdgeLookConfig | null = null;
    private edgeYawVel = 0;      // rad/s, eased toward the margin's target rate
    private edgePitchVel = 0;
    private hoverTravel = 0;     // px the cursor moved since the last frame
    private hoverSpeed = 0;      // px/s, smoothed — a fast cursor suppresses the turn
    /** Hover-look re-arms only once the cursor has left the margins after a
     *  drag (scheme D): a drag released near an edge must not keep turning. */
    private edgeArmed = true;
    /** Pointer-lock mouse-look (scheme E). null = off, the shipped behaviour. */
    private pointerLook: PointerLookConfig | null = null;
    private locked = false;

    // ── Mobile scheme capabilities (registry #3). Same rule: defaults are
    // the shipped behaviour. ──
    /** Device-orientation look. null = off. */
    private gyroLook: GyroLookConfig | null = null;
    private readonly gyro = new DeviceLook();
    /** The yaw a recenter returns to: the view when the gyro lined up. */
    private gyroHomeYaw = 0;
    /** Rad still to ease in toward the device (scheme start, recenter). */
    private gyroYawOwed = 0;
    private gyroPitchOwed = 0;
    /** A single tap's glide, waiting to see whether a second tap follows. */
    private tapTimer: number | null = null;
    private lastTapT = -1e9;
    private lastTapX = 0;
    private lastTapY = 0;
    /** Touch-and-hold anywhere walks forward toward the view direction. */
    private holdToWalk = false;
    private holding = false;
    /** Hold-the-edge (walk G): a finger resting in a margin steers the view,
     *  the touch counterpart of desktop C's cursor. null = off. */
    private edgeHold: EdgeLookConfig | null = null;
    private edgeHoldActive = false;
    /** Two fingers sliding up/down = forward/back. null = off. */
    private twoFingerMove: TwoFingerMoveConfig | null = null;
    private readonly touches = new Map<number, { x: number; y: number }>();
    private twoFingerActive = false;
    private twoFingerPrevY = 0;
    /** Joystick placement. 'floating' = appears under the thumb, left half. */
    private joystickMode: JoystickMode = 'fixed';
    private floatId: number | null = null;
    private floatCX = 0;
    private floatCY = 0;
    private floatEl: HTMLElement | null = null;
    private floatKnob: HTMLElement | null = null;

    // Gesture attribution for the practice tutorial: which TAUGHT inputs the
    // user has actually performed. Click-to-glide moves the camera too, but
    // it must not check off "WASD or scroll to move" — the card teaches
    // specific gestures, so only those gestures may arm their row.
    gestureLook = false;

    gestureWheel = false;

    gestureKeys = false;

    gestureJoystick = false;

    gestureHold = false;

    gestureTwoFinger = false;

    /** A click / tap actually started a glide. */
    gestureClickMove = false;

    /** Trackpad pinch or a two-finger spread walked the camera. */
    gesturePinch = false;

    // click-to-move slide
    private sliding = false;
    private slideElapsed = 0;
    private slideDur = 0;
    private slideFromX = 0; private slideFromZ = 0;
    private slideToX = 0; private slideToZ = 0;
    // A glide to another walk plane (more than a step up or down: the stage,
    // the upper floor) can't be walked, so it flies: XZ and feet height ease
    // together from the plane you're on to the clicked one, no collision.
    private hopping = false;
    private hopFromY = 0; private hopToY = 0; private hopY = 0;
    // Frames a glide has been pinned in place by the surface resolve (e.g.
    // clicking an unreachable storey — the invisible wall stops the slide).
    private slideStuckFrames = 0;

    // hover floor target
    private readonly _markAxis = new pc.Vec3();
    private readonly _markRot = new pc.Quat();
    private _markRay: { vox: VoxelCollider | null; ro: number[]; rd: number[]; t: number } =
        { vox: null, ro: [], rd: [], t: NaN };
    private targetX = 0; private targetZ = 0; private targetY = 0; private targetValid = false;
    private indicator: pc.Entity | null = null;
    private indicatorT = 0;

    private currentY = NaN;
    private canvas: HTMLCanvasElement | null = null;
    private cleanupFns: Array<() => void> = [];

    // ── Drag-look state (mouse + touch share one scheme) ─────────────────
    // DRAG = relative look (FPS style); a quick release with little travel is
    // a TAP/CLICK = glide to the picked floor point. One state block for both
    // pointer types: a held mouse button behaves exactly like a finger on
    // glass, which also makes the tutorial one story instead of two.
    private dragId: number | null = null;

    private dragStartX = 0;

    private dragStartY = 0;

    private dragStartT = 0;

    private dragPrevX = 0;

    private dragPrevY = 0;

    private dragMoved = 0;

    // scratch
    private readonly _near = new pc.Vec3();
    private readonly _far = new pc.Vec3();
    private readonly _dir = new pc.Vec3();

    constructor(camera: pc.Entity, opts: WalkOptions) {
        this.camera = camera;
        this.app = opts.app;
        // Ground floor + extra levels + stair ramps, flattened into one
        // surface list (index 0 = ground). Old single-floor scenes yield
        // exactly one surface.
        this.surfaces = buildWalkSurfaces(opts.walk);
        this.eyeHeight = (typeof opts.walk.eyeHeight === 'number' && opts.walk.eyeHeight > 0)
            ? opts.walk.eyeHeight : CANONICAL_EYE_HEIGHT;
        this.createIndicator();
        this.loadScrollSign();
    }

    private loadScrollSign(): void {
        try {
            const v = window.localStorage.getItem(SCROLL_SIGN_KEY);
            if (v === '1' || v === '-1') { this.scrollSign = Number(v); this.scrollSignKnown = true; return; }
        } catch { /* storage blocked: guess */ }
        // macOS ships with natural scrolling on; Windows/Linux mice and most
        // touchpads default to classic. Only a guess: the tour corrects it.
        const plat = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform
            ?? navigator.platform ?? '';
        this.scrollSign = /mac/i.test(plat) ? -1 : 1;
    }

    /** Drag pulls the picture (true, default) or turns the head (FPS). */
    setGrabLook(on: boolean): void {
        this.grabLook = on;
    }

    /** Touch two-finger walk / pinch-to-approach. On by default. */
    setTwoFingerNav(on: boolean): void {
        this.twoFingerNav = on;
        if (!on) this.tfMode = 'undecided';
    }

    get scrollDirectionKnown(): boolean {
        return this.scrollSignKnown;
    }

    /**
     * The tour has just asked "slide two fingers forward": the next real
     * vertical scroll tells us which way this machine's scrolling runs, and
     * we keep it for every later visit. A mouse wheel rolled forward answers
     * the same question.
     */
    startScrollCalibration(): void {
        this.calibrating = true;
        this.calibAccum = 0;
    }

    /** Derive yaw/pitch from a start pose by pointing the camera at `target`. */
    initFromPose(position: pc.Vec3, target: pc.Vec3): void {
        this.camera.setPosition(position);
        // Derive yaw/pitch straight from the view direction — do NOT round-trip
        // through getLocalEulerAngles. PlayCanvas decomposes euler-Y with asin
        // (±90°), so a look along ±X sits at gimbal lock and the pitch/roll split
        // is unstable; dropping roll there flipped the camera ~60° up. The
        // controller renders orientation as setLocalEulerAngles(pitch, yaw, 0),
        // whose forward is (-cos p·sin y, sin p, -cos p·cos y) — invert exactly.
        const dx = target.x - position.x;
        const dy = target.y - position.y;
        const dz = target.z - position.z;
        const len = Math.hypot(dx, dy, dz) || 1;
        this.pitch = Math.asin(Math.max(-1, Math.min(1, dy / len)));
        this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch));
        this.yaw = Math.atan2(-dx, -dz);
        // Re-seed on the next update; the pose's height picks WHICH floor the
        // camera stands on when levels overlap in XZ.
        this.seedFeetY = position.y - this.eyeHeight;
        this.feetY = NaN;
        this.currentY = NaN;
    }

    /**
     * Walk on collision voxels (or back to plane-only with null). The voxels
     * load after the splat, so this re-seeds where the camera stands now.
     */
    /** The collision voxels walked on, once loaded (the measure tool picks on them). */
    get collisionVoxels(): VoxelCollider | null {
        return this.voxels;
    }

    setVoxels(collider: VoxelCollider | null): void {
        this.voxels = collider;
        this.voxelPoint = null;
        if (!Number.isNaN(this.feetY)) this.seedFeetY = this.feetY;
        this.feetY = NaN;
    }

    /**
     * Show the on-screen joystick. Toggling while attached creates/removes it
     * live, so the cog can switch schemes mid-session.
     */
    setJoystickEnabled(on: boolean): void {
        if (this.joystickAllowed === on) return;
        this.joystickAllowed = on;
        if (!on) this.destroyJoystick();
        else if (this.cleanupFns.length > 0) this.createJoystick();  // attached
    }

    /** Click/tap a floor point to glide there — and the hover disc that
     *  advertises it. Off removes both, and kills any glide in flight. */
    get clickToMoveEnabled(): boolean {
        return this.clickToMove;
    }

    setClickToMoveEnabled(on: boolean): void {
        this.clickToMove = on;
        if (on) return;
        this.sliding = false;
        this.targetValid = false;
        if (this.indicator) this.indicator.enabled = false;
    }

    /** Multiplier applied to WASD walk speed while Shift is held. Keyboard
     *  only — the wheel is deliberately untouched, because Shift+wheel is the
     *  browser's horizontal-scroll gesture and never arrives as a plain
     *  deltaY, so a wheel boost could only ever be flaky. */
    setShiftBoost(mult: number): void {
        this.shiftBoost = Number.isFinite(mult) && mult > 0 ? mult : 1;
    }

    /** Scales the wheel dolly. A trackpad hands over an order of magnitude
     *  more delta per gesture than a notched wheel (and keeps going on
     *  momentum), so a scheme can tone the whole axis down. */
    setWheelSensitivity(mult: number): void {
        this.wheelSensitivity = Number.isFinite(mult) && mult > 0 ? mult : 1;
    }

    /** Press-and-drag to look. Off, moving a pressed pointer turns nothing
     *  (click-to-move is gated separately; C turns both off). The cursor
     *  follows: the view only reads as "grabbable" while this is on. */
    get dragLookEnabled(): boolean {
        return this.dragLook;
    }

    setDragLookEnabled(on: boolean): void {
        this.dragLook = on;
        if (this.canvas) this.canvas.style.cursor = on ? 'grab' : '';
    }

    /** Hover-to-look (scheme C). null restores the shipped behaviour. */
    setEdgeLook(cfg: EdgeLookConfig | null): void {
        this.edgeLook = cfg;
        if (!cfg) this.resetEdgeLook();
    }

    private resetEdgeLook(): void {
        this.edgeYawVel = 0;
        this.edgePitchVel = 0;
        this.hoverTravel = 0;
        this.hoverSpeed = 0;
        this.edgeArmed = true;
    }

    /** Pointer-lock mouse-look (scheme E). null restores the shipped
     *  behaviour and releases a lock in progress. */
    setPointerLook(cfg: PointerLookConfig | null): void {
        this.pointerLook = cfg;
        if (!cfg) this.releasePointerLock();
    }

    private releasePointerLock(): void {
        if (!this.locked) return;
        try { document.exitPointerLock(); } catch { /* nothing to release */ }
        this.locked = false;
    }

    // ── Mobile scheme setters ──────────────────────────────────────────────

    /** Device-orientation look. Must be called from a user gesture the first
     *  time (iOS permission) — the scheme tile's tap is one. */
    setGyroLook(cfg: GyroLookConfig | null): void {
        this.gyroLook = cfg;
        this.gyroYawOwed = 0;
        this.gyroPitchOwed = 0;
        if (cfg) this.gyro.start();
        else this.gyro.stop();
    }

    /** Gyro schemes' double-tap: face the way the view faced when the gyro
     *  lined up, level with the device again (upright = horizon). */
    private recenterGyro(): void {
        const devicePitch = this.gyro.pitch();
        if (!this.gyroLook || devicePitch === null) return;
        const turn = this.gyroHomeYaw - this.yaw;
        this.gyroYawOwed = turn - 2 * Math.PI * Math.round(turn / (2 * Math.PI));
        this.gyroPitchOwed = this.gyroPitchTarget(devicePitch) - this.pitch;
    }

    private gyroPitchTarget(devicePitch: number): number {
        return Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, devicePitch * (this.gyroLook?.pitchGain ?? 1)));
    }

    /** Touch-and-hold to walk forward. */
    setHoldToWalk(on: boolean): void {
        this.holdToWalk = on;
        if (!on) this.holding = false;
    }

    /** Hold-the-edge steering (walk G): side margins turn, top/bottom tilt. */
    setEdgeHold(cfg: EdgeLookConfig | null): void {
        this.edgeHold = cfg;
        this.edgeHoldActive = false;
        this.resetEdgeLook();
    }

    /** Two-finger slide to dolly. */
    setTwoFingerMove(cfg: TwoFingerMoveConfig | null): void {
        this.twoFingerMove = cfg;
        if (!cfg) this.twoFingerActive = false;
    }

    /** Fixed pad (shipped) or a pad that appears under the thumb on the
     *  left half. Floating implies the fixed pad is gone; the joystick
     *  still has to be ENABLED for either to exist. */
    setJoystickMode(mode: JoystickMode): void {
        if (this.joystickMode === mode) return;
        this.joystickMode = mode;
        this.destroyJoystick();
        this.endFloatingJoystick();
        if (mode === 'fixed' && this.cleanupFns.length > 0) this.createJoystick();
    }

    attach(canvas: HTMLCanvasElement): void {
        if (this.cleanupFns.length > 0) return;
        this.canvas = canvas;
        // The device kept turning while we were detached (tour, panel) —
        // drop that delta or the view snaps on resume.
        this.gyro.consume();

        // Drag-look tuning: ~0.005 rad/px on touch ≈ a full phone-width drag
        // turns ~110°; the mouse runs slightly slower (precise device, longer
        // sweeps). A tap/click is <10px of travel within 350ms.
        const TOUCH_LOOK_SENS = 0.005;
        const MOUSE_LOOK_SENS = 0.0035;
        const TAP_MAX_PX = 10;
        const TAP_MAX_MS = 350;

        // Glide the camera to the floor point under screen (x, y) — the
        // click-to-move used by mouse pointerdown and by touch TAPS.
        const startGlideAt = (x: number, y: number) => {
            this.mouseX = x; this.mouseY = y;
            // updateFloorTarget gates on mouseIn (so a stale hover indicator
            // can't linger). Touch never sets mouseIn — satisfy the gate just
            // for this raycast, then restore so the indicator stays mouse-only.
            const wasIn = this.mouseIn;
            this.mouseIn = true;
            this.updateFloorTarget();
            this.mouseIn = wasIn;
            if (this.targetValid) {
                const p = this.camera.getPosition();
                const dist = Math.hypot(this.targetX - p.x, this.targetZ - p.z);
                this.slideFromX = p.x; this.slideFromZ = p.z;
                this.slideToX = this.targetX; this.slideToZ = this.targetZ;
                this.slideElapsed = 0;
                this.hopping = !Number.isNaN(this.feetY) && Math.abs(this.targetY - this.feetY) > STEP_TOLERANCE;
                this.hopFromY = this.hopY = this.feetY;
                this.hopToY = this.targetY;
                this.slideDur = this.hopping
                    ? Math.max(0.8, Math.min(2.5, (dist + Math.abs(this.hopToY - this.hopFromY)) * 0.4))
                    : Math.max(0.4, Math.min(2.0, dist * 0.4));
                this.sliding = true;
                this.slideStuckFrames = 0;
                this.gestureClickMove = true;
            }
        };

        const onMove = (e: PointerEvent) => {
            if (this.locked && this.pointerLook && e.pointerType !== 'touch') {
                // Pointer-locked: raw motion IS the look. Clamped per event —
                // the first move after a lock can carry a bogus jump on some
                // browsers, and a snap to the ceiling teaches nobody anything.
                const s = this.pointerLook.sens;
                const mx = Math.max(-120, Math.min(120, e.movementX));
                const my = Math.max(-120, Math.min(120, e.movementY));
                this.yaw -= mx * s;
                this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch - my * s));
                if (Math.abs(mx) + Math.abs(my) > 2) this.gestureLook = true;
                return;
            }
            if (e.pointerType === 'touch') {
                const t = this.touches.get(e.pointerId);
                if (t) { t.x = e.clientX; t.y = e.clientY; }
                // Floating joystick: this finger IS the pad.
                if (e.pointerId === this.floatId) {
                    this.updateFloatingJoystick(e.clientX, e.clientY);
                    return;
                }
                if (this.twoFingerActive && !this.twoFingerMove && t) {
                    this.updateTwoFingerNav();
                    return;
                }
                // Two-finger dolly: the pair's centroid sliding up = forward.
                if (this.twoFingerActive && this.twoFingerMove && t) {
                    const cy = this.touchCentroidY();
                    const dy = cy - this.twoFingerPrevY;
                    this.twoFingerPrevY = cy;
                    this.scrollVel += -dy * this.twoFingerMove.sens;
                    this.sliding = false;
                    if (Math.abs(dy) > 2) this.gestureTwoFinger = true;
                    return;
                }
            } else {
                // Hover position: the floor indicator and edge-look both read
                // it. Tracked through a press too, so a held button can't
                // freeze the position the edge-look steers by. (Touch never
                // hovers — a stray second finger must not fake one.)
                if (this.edgeLook && this.mouseIn) {
                    this.hoverTravel += Math.abs(e.clientX - this.mouseX) + Math.abs(e.clientY - this.mouseY);
                }
                this.mouseX = e.clientX; this.mouseY = e.clientY;
                this.mouseIn = true;
            }
            if (e.pointerId === this.dragId) {
                // Relative look (FPS-style) while the pointer is held down.
                const dx = e.clientX - this.dragPrevX;
                const dy = e.clientY - this.dragPrevY;
                this.dragPrevX = e.clientX; this.dragPrevY = e.clientY;
                this.dragMoved += Math.abs(dx) + Math.abs(dy);
                // An edge hold steers by where the finger IS, not by how it
                // moves — a drifting thumb must not also swipe the view.
                if (this.edgeHoldActive) return;
                // Drag-look off: the press can still end as a click, but
                // moving it turns nothing.
                if (!this.dragLook) return;
                // Looking around does not cancel a glide: it only turns the
                // view, and the glide runs to a fixed point on the floor.
                if (this.dragMoved > TAP_MAX_PX) this.gestureLook = true; // a real drag, not a click
                // Grab: the picture follows the hand, so the view turns the
                // OTHER way (drag right → look left, drag down → look up).
                const sens = (e.pointerType === 'touch' ? TOUCH_LOOK_SENS : MOUSE_LOOK_SENS) * (this.grabLook ? -1 : 1);
                this.yaw -= dx * sens;
                this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch - dy * sens));
            }
        };
        const onEnter = (e: PointerEvent) => {
            if (e.pointerType !== 'mouse') return; // the indicator is mouse-only
            this.mouseX = e.clientX;
            this.mouseY = e.clientY;
            this.mouseIn = true;
        };
        const onLeave = () => { this.mouseIn = false; if (this.indicator) this.indicator.enabled = false; };
        const onDown = (e: PointerEvent) => {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (e.pointerType === 'touch') {
                this.touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
                // Floating joystick: a finger landing on the LEFT half is the
                // pad; the right half stays the look-drag.
                if (this.joystickAllowed && this.joystickMode === 'floating' && this.floatId === null && !hasFinePointer()) {
                    const r = canvas.getBoundingClientRect();
                    if (e.clientX - r.left < r.width / 2) {
                        this.startFloatingJoystick(e.pointerId, e.clientX, e.clientY);
                        try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
                        return;
                    }
                }
                // Two-finger dolly: the second finger ends any look-drag (and
                // a would-be tap) and the pair starts moving the camera.
                if ((this.twoFingerMove || this.twoFingerNav) && this.touches.size === 2) {
                    this.twoFingerActive = true;
                    this.twoFingerPrevY = this.touchCentroidY();
                    this.beginTwoFingerNav();
                    this.dragId = null;
                    this.holding = false;
                    try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
                    return;
                }
            }
            // Don't act yet — decide on release: tap/click → glide, drag → look.
            this.dragId = e.pointerId;
            this.dragStartX = this.dragPrevX = e.clientX;
            this.dragStartY = this.dragPrevY = e.clientY;
            // Event INPUT time, not performance.now() at handler run time: a
            // single slow frame between press and release adds render-stall
            // time to the wall clock and turns real clicks into "long
            // presses" that never glide.
            this.dragStartT = e.timeStamp;
            this.dragMoved = 0;
            this.edgeHoldActive = false;
            if (e.pointerType === 'mouse' && this.dragLook) canvas.style.cursor = 'grabbing';
            try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
        };
        const onUp = (e: PointerEvent) => {
            if (e.pointerType === 'touch') {
                this.touches.delete(e.pointerId);
                if (e.pointerId === this.floatId) {
                    this.endFloatingJoystick();
                    if (canvas.hasPointerCapture(e.pointerId)) {
                        try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
                    }
                    return;
                }
                if (this.twoFingerActive && this.touches.size < 2) {
                    // The remaining finger must not turn into a look-drag jump
                    // or a tap — the gesture is over.
                    this.twoFingerActive = false;
                    this.dragId = null;
                    if (canvas.hasPointerCapture(e.pointerId)) {
                        try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
                    }
                    return;
                }
            }
            if (e.pointerId !== this.dragId) return;
            this.dragId = null;
            this.holding = false;
            this.edgeHoldActive = false;
            if (e.pointerType === 'mouse') canvas.style.cursor = this.dragLook ? 'grab' : '';
            if (canvas.hasPointerCapture(e.pointerId)) {
                try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            }
            const quick = (e.timeStamp - this.dragStartT) < TAP_MAX_MS;
            const tap = quick && this.dragMoved < TAP_MAX_PX;
            if (tap && this.gyroLook && e.pointerType === 'touch') {
                // Gyro schemes: two quick taps close together = recenter.
                const x = this.dragStartX, y = this.dragStartY;
                const second = this.tapTimer !== null &&
                    e.timeStamp - this.lastTapT < DOUBLE_TAP_MS &&
                    Math.hypot(x - this.lastTapX, y - this.lastTapY) < DOUBLE_TAP_PX;
                if (this.tapTimer !== null) window.clearTimeout(this.tapTimer);
                this.tapTimer = null;
                if (second) {
                    this.recenterGyro();
                } else {
                    this.lastTapT = e.timeStamp;
                    this.lastTapX = x;
                    this.lastTapY = y;
                    this.tapTimer = window.setTimeout(() => {
                        this.tapTimer = null;
                        if (this.clickToMove) startGlideAt(x, y);
                    }, DOUBLE_TAP_MS);
                }
            } else if (this.clickToMove && tap) {
                startGlideAt(this.dragStartX, this.dragStartY);
            }
            // A real drag disarms hover-look until the cursor has left the
            // margins: released near an edge, the view must stay put.
            if (this.dragLook && this.dragMoved > TAP_MAX_PX) this.edgeArmed = false;
        };
        const onKeyDown = (e: KeyboardEvent) => {
            // Read modifier state off every key event rather than tracking
            // Shift's own down/up: releasing Shift over another window would
            // otherwise leave the boost stuck on until the next Shift press.
            this.shiftDown = e.shiftKey;
            const k = e.key.toLowerCase();
            if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) {
                this.keys.add(k);
                this.gestureKeys = true;
                this.sliding = false; // manual movement cancels an active glide
            }
        };
        const onKeyUp = (e: KeyboardEvent) => {
            this.shiftDown = e.shiftKey;
            this.keys.delete(e.key.toLowerCase());
        };
        const onContext = (e: MouseEvent) => e.preventDefault();
        const onWheel = (e: WheelEvent) => {
            // Smooth dolly along the view direction (Spark parity). Ignored
            // mid-glide so a click-to-move isn't fought by the wheel.
            e.preventDefault();
            if (this.sliding) return;
            // Trackpad pinch (Chrome/Edge/Firefox/Safari all send it as
            // ctrl+wheel): spread = negative deltaY = walk toward the cursor.
            if (e.ctrlKey) {
                this.pinchToward(e.clientX, e.clientY, -e.deltaY * PINCH_WHEEL_SENS);
                this.gesturePinch = true;
                return;
            }
            const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
            const dy = e.deltaY * unit;
            const dx = e.deltaX * unit;
            if (this.calibrating && Math.abs(dy) > Math.abs(dx)) {
                // Asked to slide FORWARD: classic scrolling reports that as
                // negative deltaY, natural scrolling as positive.
                this.calibAccum += dy;
                if (Math.abs(this.calibAccum) > 60) {
                    this.scrollSign = this.calibAccum < 0 ? 1 : -1;
                    this.scrollSignKnown = true;
                    this.calibrating = false;
                    try { window.localStorage.setItem(SCROLL_SIGN_KEY, String(this.scrollSign)); } catch { /* fine */ }
                }
            }
            // A notched wheel jumps in big whole steps with no sideways
            // component; anything else is a trackpad and gets the calm factor.
            const notch = dx === 0 && Math.abs(dy) >= 50 && Number.isInteger(dy);
            const k = (notch ? WHEEL_NOTCH_SENS : WHEEL_TRACKPAD_SENS) * this.wheelSensitivity;
            // Finger direction: up = forward, right = step right.
            this.scrollVel += -dy * this.scrollSign * k;
            this.strafeVel += dx * this.scrollSign * k;
            this.gestureWheel = true;
        };

        // Pointer-lock mouse-look (scheme E): a click is the only way in —
        // browsers demand a user gesture — and Esc (the browser's own exit) or
        // a detach is the way out. Inert unless a scheme configured it.
        const onClick = (e: MouseEvent) => {
            if (!this.pointerLook || this.locked || e.button !== 0) return;
            try {
                // Denied = no gesture, an iframe without allow="pointer-lock",
                // or the cooldown browsers impose right after an Esc exit.
                void Promise.resolve(canvas.requestPointerLock()).catch(() => {});
            } catch { /* unsupported */ }
        };
        const onLockChange = () => {
            this.locked = document.pointerLockElement === canvas;
        };

        // The view is grabbable — say so with the cursor.
        const prevCursor = canvas.style.cursor;
        canvas.style.cursor = this.dragLook ? 'grab' : '';
        this.cleanupFns.push(() => { canvas.style.cursor = prevCursor; });

        canvas.addEventListener('pointermove', onMove);
        canvas.addEventListener('pointerenter', onEnter);
        canvas.addEventListener('pointerleave', onLeave);
        canvas.addEventListener('pointerdown', onDown);
        canvas.addEventListener('pointerup', onUp);
        canvas.addEventListener('pointercancel', onUp);
        canvas.addEventListener('contextmenu', onContext);
        canvas.addEventListener('wheel', onWheel, { passive: false });
        canvas.addEventListener('click', onClick);
        document.addEventListener('pointerlockchange', onLockChange);
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);

        // Joystick stuck-stick safety net: any pointer release or cancel,
        // ANYWHERE (window, capture phase, so nothing can swallow it), ends a
        // stick owned by that pointer — pointer capture on the pad is the
        // normal path but is not guaranteed, and a missed release left the
        // camera walking on its own. Focus loss ends every stick outright.
        const onAnyPointerEnd = (e: PointerEvent) => {
            if (this.joyPointerId === e.pointerId || this.floatId === e.pointerId) {
                this.resetSticks();
            }
        };
        const onWindowBlur = () => this.resetSticks();
        window.addEventListener('pointerup', onAnyPointerEnd, true);
        window.addEventListener('pointercancel', onAnyPointerEnd, true);
        window.addEventListener('blur', onWindowBlur);

        this.cleanupFns.push(
            () => canvas.removeEventListener('pointermove', onMove),
            () => canvas.removeEventListener('pointerenter', onEnter),
            () => canvas.removeEventListener('pointerleave', onLeave),
            () => canvas.removeEventListener('pointerdown', onDown),
            () => canvas.removeEventListener('pointerup', onUp),
            () => canvas.removeEventListener('pointercancel', onUp),
            () => canvas.removeEventListener('contextmenu', onContext),
            () => canvas.removeEventListener('wheel', onWheel),
            () => canvas.removeEventListener('click', onClick),
            () => document.removeEventListener('pointerlockchange', onLockChange),
            () => window.removeEventListener('keydown', onKeyDown),
            () => window.removeEventListener('keyup', onKeyUp),
            () => window.removeEventListener('pointerup', onAnyPointerEnd, true),
            () => window.removeEventListener('pointercancel', onAnyPointerEnd, true),
            () => window.removeEventListener('blur', onWindowBlur)
        );

        this.createJoystick();
    }

    detach(): void {
        for (const fn of this.cleanupFns) fn();
        this.cleanupFns = [];
        if (this.tapTimer !== null) window.clearTimeout(this.tapTimer);
        this.tapTimer = null;
        this.keys.clear();
        this.shiftDown = false;
        // The leave listener went with the rest, so a pointer that wanders off
        // while detached could never clear this — and edge-look would steer by
        // a stale position on resume. The next move re-arms it.
        this.mouseIn = false;
        this.resetEdgeLook();
        this.releasePointerLock();
        if (this.indicator) this.indicator.enabled = false;
        this.destroyJoystick();
        this.endFloatingJoystick();
        this.touches.clear();
        this.twoFingerActive = false;
        this.tfMode = 'undecided';
        this.holding = false;
        this.edgeHoldActive = false;
        this.dragId = null;
        this.scrollVel = 0; this.strafeVel = 0; this.pinchVX = 0; this.pinchVZ = 0;
    }

    update(dt: number): void {
        // Hover-to-look is the one per-frame look input (drag sets yaw/pitch
        // directly in its handler). null in the shipped controller.
        if (this.edgeLook) this.updateEdgeLook(dt, this.edgeLook);
        else if (this.edgeHold) this.updateEdgeHold(dt, this.edgeHold);
        // Device-orientation look (mobile B/F): integrate the device's turn
        // since last frame, so a swipe on top of it composes instead of
        // fighting. Device turned right = look right = yaw decreases.
        if (this.gyroLook) {
            if (this.gyro.takeAlign()) {
                // First reading: level the view with the device (upright =
                // horizon), whatever pose the scheme tap caught it in.
                this.gyroHomeYaw = this.yaw;
                const devicePitch = this.gyro.pitch();
                if (devicePitch !== null) this.gyroPitchOwed = this.gyroPitchTarget(devicePitch) - this.pitch;
            }
            const { dHeading, dPitch } = this.gyro.consume();
            if (dHeading !== 0 || dPitch !== 0) {
                this.yaw -= dHeading * this.gyroLook.yawGain;
                this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch + dPitch * this.gyroLook.pitchGain));
                if (Math.abs(dHeading) + Math.abs(dPitch) > 0.002) this.gestureLook = true;
            }
            if (this.gyroYawOwed !== 0 || this.gyroPitchOwed !== 0) {
                const k = Math.min(1, dt * GYRO_ALIGN_RATE);
                const dy = this.gyroYawOwed * k, dp = this.gyroPitchOwed * k;
                this.yaw += dy;
                this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch + dp));
                this.gyroYawOwed -= dy;
                this.gyroPitchOwed -= dp;
                if (Math.abs(this.gyroYawOwed) + Math.abs(this.gyroPitchOwed) < 1e-4) {
                    this.gyroYawOwed = 0;
                    this.gyroPitchOwed = 0;
                }
            }
        }
        // Apply orientation first so forward/right reflect this frame's view.
        this.camera.setLocalEulerAngles(this.pitch * RAD2DEG, this.yaw * RAD2DEG, 0);

        // Hold-to-walk (mobile C/F): a finger that has sat still for HOLD_MS
        // becomes a held "forward"; it stays held if the finger then drifts,
        // so hold-and-steer works.
        if (this.holdToWalk && !this.holding && this.dragId !== null &&
            this.dragMoved < HOLD_MAX_PX && performance.now() - this.dragStartT > HOLD_MS) {
            this.holding = true;
            this.sliding = false;
            this.gestureHold = true;
        }

        // ── Movement (WASD velocity-lerp), or an active click-to-move glide ──
        const p = this.camera.getPosition();
        let nextX = p.x;
        let nextZ = p.z;

        let tf = 0, tr = 0;
        if (this.keys.has('w') || this.keys.has('arrowup')) tf += 1;
        if (this.keys.has('s') || this.keys.has('arrowdown')) tf -= 1;
        if (this.keys.has('a') || this.keys.has('arrowleft')) tr -= 1;
        if (this.keys.has('d') || this.keys.has('arrowright')) tr += 1;
        // Virtual joystick feeds the same target velocity (Spark parity).
        tf += this.joystickY;
        tr += this.joystickX;
        if (this.holding) tf += 1;
        const moving = tf !== 0 || tr !== 0;
        const m = Math.hypot(tf, tr);
        if (m > 1) { tf /= m; tr /= m; }

        const speed = WALK_SPEED * (this.shiftDown ? this.shiftBoost : 1);
        this.velF += (tf * speed - this.velF) * WALK_ACCEL;
        this.velR += (tr * speed - this.velR) * WALK_ACCEL;
        if (Math.abs(this.velF) < VEL_DEADBAND && tf === 0) this.velF = 0;
        if (Math.abs(this.velR) < VEL_DEADBAND && tr === 0) this.velR = 0;

        if (this.velF !== 0 || this.velR !== 0) {
            const fwd = this.camera.forward;
            const right = this.camera.right;
            // Horizontal projection so looking up/down doesn't change speed.
            let fx = fwd.x, fz = fwd.z;
            const fl = Math.hypot(fx, fz) || 1; fx /= fl; fz /= fl;
            let rx = right.x, rz = right.z;
            const rl = Math.hypot(rx, rz) || 1; rx /= rl; rz /= rl;
            nextX += (fx * this.velF + rx * this.velR) * dt;
            nextZ += (fz * this.velF + rz * this.velR) * dt;
        }

        if (this.sliding && !moving) {
            this.slideElapsed += dt;
            const t = smoothstep(Math.min(this.slideElapsed / this.slideDur, 1));
            nextX = this.slideFromX + (this.slideToX - this.slideFromX) * t;
            nextZ = this.slideFromZ + (this.slideToZ - this.slideFromZ) * t;
            if (this.hopping) this.hopY = this.hopFromY + (this.hopToY - this.hopFromY) * t;
            if (this.slideElapsed >= this.slideDur) this.sliding = false;
        }

        // ── Wheel dolly along the horizontal view direction (Spark parity) ──
        // Height still comes from the floor plane, XZ is still boundary-clamped.
        const cap = (v: number) => Math.max(-IMPULSE_MAX, Math.min(IMPULSE_MAX, v));
        this.scrollVel = cap(this.scrollVel);
        this.strafeVel = cap(this.strafeVel);
        if (Math.abs(this.scrollVel) > 1e-3) {
            const fwd = this.camera.forward;
            let fx = fwd.x, fz = fwd.z;
            const fl = Math.hypot(fx, fz) || 1; fx /= fl; fz /= fl;
            nextX += fx * this.scrollVel;
            nextZ += fz * this.scrollVel;
            this.scrollVel *= 0.92;
        } else {
            this.scrollVel = 0;
        }
        if (Math.abs(this.strafeVel) > 1e-3) {
            const right = this.camera.right;
            let rx = right.x, rz = right.z;
            const rl = Math.hypot(rx, rz) || 1; rx /= rl; rz /= rl;
            nextX += rx * this.strafeVel;
            nextZ += rz * this.strafeVel;
            this.strafeVel *= 0.92;
        } else {
            this.strafeVel = 0;
        }
        const pm = Math.hypot(this.pinchVX, this.pinchVZ);
        if (pm > 1e-3) {
            const s = pm > IMPULSE_MAX ? IMPULSE_MAX / pm : 1;
            this.pinchVX *= s; this.pinchVZ *= s;
            nextX += this.pinchVX;
            nextZ += this.pinchVZ;
            this.pinchVX *= 0.9; this.pinchVZ *= 0.9;
        } else {
            this.pinchVX = 0; this.pinchVZ = 0;
        }

        // ── Resolve against the walkable surfaces (levels + stairs) ──
        // XZ clamps to whatever is reachable from the current floor height;
        // the floor height under the camera follows the resolved surface
        // (level or stair ramp) and the camera Y eases toward it. A glide
        // whose straight path crosses a stairs quad climbs it naturally —
        // the ramp height stays within the step tolerance frame to frame.
        let r: { x: number; z: number; y: number };
        if (this.hopping) {
            r = { x: nextX, z: nextZ, y: this.hopY };
        } else if (this.voxels) {
            this.voxelPoint = (Number.isNaN(this.feetY) || !this.voxelPoint)
                ? seedVoxelWalkPoint(this.voxels, this.surfaces, nextX, nextZ, this.seedFeetY)
                : resolveVoxelWalkPoint(this.voxels, this.surfaces, this.eyeHeight, this.voxelPoint, nextX, nextZ);
            r = this.voxelPoint;
        } else {
            r = Number.isNaN(this.feetY)
                ? seedWalkPoint(this.surfaces, nextX, nextZ, this.seedFeetY)
                : resolveWalkPoint(this.surfaces, this.feetY, nextX, nextZ);
        }
        // A glide pinned in place by the resolve (clicked an unreachable
        // storey → invisible wall) is going nowhere: cancel it after a few
        // stuck frames instead of pressing into the wall for the whole slide.
        if (this.sliding && !this.hopping) {
            const moved = Math.hypot(r.x - p.x, r.z - p.z);
            this.slideStuckFrames = moved < 1e-4 ? this.slideStuckFrames + 1 : 0;
            if (this.slideStuckFrames >= 3) this.sliding = false;
        }
        this.feetY = r.y;
        if (this.hopping && !this.sliding) {
            // landed (or the hop was cut short): stand on the walk plane
            // under this point from the next frame, nearest the hop height
            this.hopping = false;
            this.feetY = NaN;
            this.voxelPoint = null;
            this.seedFeetY = r.y;
        }
        const desiredY = r.y + this.eyeHeight;
        if (Number.isNaN(this.currentY)) this.currentY = desiredY;
        else this.currentY += (desiredY - this.currentY) * Y_LERP;
        this.camera.setPosition(r.x, this.currentY, r.z);

        this.updateFloorTarget();
    }

    // ── internals ──────────────────────────────────────────────────────────

    /**
     * Scheme C's look input. The cursor's depth into a margin sets a TARGET
     * turn rate — eased, so the margin's inner edge is a drift and the screen
     * edge the full rate — and the actual rate chases it, so entering or
     * leaving a margin never snaps. Two things zero the target: a cursor that
     * is moving fast (it is going somewhere, not looking), and the toolbar's
     * corner.
     */
    /**
     * Walk G. A finger on the view steers like desktop C's cursor: sitting in
     * a side margin turns, in the top/bottom margin tilts, faster the deeper
     * it sits, until it lifts. With drag-look off (G) that is all a finger
     * does, so it acts the moment it lands or slides into a margin. With
     * drag-look on, a finger must first land in a margin and sit still for
     * HOLD_MS, so swipes keep working.
     */
    private updateEdgeHold(dt: number, cfg: EdgeLookConfig): void {
        const rect = this.canvas?.getBoundingClientRect();
        let targetYaw = 0;
        let targetPitch = 0;
        if (rect && rect.width > 0 && rect.height > 0 && this.dragId !== null) {
            const m = cfg.margin;
            // Signed depth into a margin: 0 at its inner boundary, ±1 at the
            // screen edge. Negative = left / top.
            const depth = (n: number) => n < m ? -(m - n) / m : n > 1 - m ? (n - (1 - m)) / m : 0;
            const ax = (x: number) => depth((x - rect.left) / rect.width);
            const ay = (y: number) => depth((y - rect.top) / rect.height);
            if (!this.edgeHoldActive) {
                if (!this.dragLook) {
                    this.edgeHoldActive = true;
                } else if (this.dragMoved < HOLD_MAX_PX && performance.now() - this.dragStartT > HOLD_MS &&
                    (ax(this.dragStartX) !== 0 || ay(this.dragStartY) !== 0)) {
                    this.edgeHoldActive = true;
                }
            }
            if (this.edgeHoldActive) {
                const curve = (a: number) => a === 0 ? 0 : Math.sign(a) * (cfg.floor + Math.pow(Math.min(Math.abs(a), 1), cfg.ease));
                // Right margin → turn right = yaw decreases (the drag's sign);
                // bottom margin → look down = pitch decreases.
                targetYaw = -curve(ax(this.dragPrevX)) * cfg.maxYaw;
                targetPitch = -curve(ay(this.dragPrevY)) * cfg.maxPitch;
                if (targetYaw !== 0 || targetPitch !== 0) this.gestureLook = true;
            }
        }
        this.edgeYawVel += (targetYaw - this.edgeYawVel) * 0.15;
        this.edgePitchVel += (targetPitch - this.edgePitchVel) * 0.15;
        if (Math.abs(this.edgeYawVel) < 1e-4) this.edgeYawVel = 0;
        if (Math.abs(this.edgePitchVel) < 1e-4) this.edgePitchVel = 0;
        this.yaw += this.edgeYawVel * dt;
        this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch + this.edgePitchVel * dt));
    }

    private updateEdgeLook(dt: number, cfg: EdgeLookConfig): void {
        // Cursor speed over the last frame, smoothed, so one jittery event
        // can't flicker the suppression on and off.
        const inst = dt > 0 ? this.hoverTravel / dt : 0;
        this.hoverTravel = 0;
        this.hoverSpeed += (inst - this.hoverSpeed) * 0.3;

        let targetYaw = 0;
        let targetPitch = 0;
        const rect = this.canvas?.getBoundingClientRect();
        const pressed = this.dragId !== null && this.dragLook;
        if (!this.mouseIn) this.edgeArmed = true;   // off the canvas counts as "left the margins"
        if (rect && rect.width > 0 && rect.height > 0 && this.mouseIn && !pressed) {
            const mx = this.mouseX - rect.left;
            const my = this.mouseY - rect.top;
            const inUi = mx > rect.width - cfg.uiDeadZone.w && my < cfg.uiDeadZone.h;
            if (!inUi) {
                const m = cfg.margin;
                const nx = mx / rect.width;
                const ny = my / rect.height;
                // Signed depth into the margin: 0 at its inner boundary, ±1 at
                // the screen edge. Negative = left / top.
                const ax = nx < m ? -(m - nx) / m : nx > 1 - m ? (nx - (1 - m)) / m : 0;
                const ay = ny < m ? -(m - ny) / m : ny > 1 - m ? (ny - (1 - m)) / m : 0;
                // Disarmed by a drag (scheme D): hold still until the cursor
                // has been out of the margins once, then behave as normal.
                if (!this.edgeArmed && ax === 0 && ay === 0) this.edgeArmed = true;
                const gain = this.edgeArmed ? 1 - Math.min(this.hoverSpeed / cfg.suppressSpeed, 1) : 0;
                // Cubic on a DC floor: crossing into the margin starts the
                // turn at `floor` of the peak straight away, and the curve
                // climbs on top of that — the inner margin is never dead.
                const ease = (a: number) => a === 0 ? 0 : Math.sign(a) * (cfg.floor + Math.pow(Math.min(Math.abs(a), 1), cfg.ease));
                // Right margin → turn right = yaw decreases (the drag handler's
                // sign); bottom margin → look down = pitch decreases.
                targetYaw = -ease(ax) * cfg.maxYaw * gain;
                targetPitch = -ease(ay) * cfg.maxPitch * gain;
            }
        }

        this.edgeYawVel += (targetYaw - this.edgeYawVel) * 0.15;
        this.edgePitchVel += (targetPitch - this.edgePitchVel) * 0.15;
        if (Math.abs(this.edgeYawVel) < 1e-4) this.edgeYawVel = 0;
        if (Math.abs(this.edgePitchVel) < 1e-4) this.edgePitchVel = 0;
        if (this.edgeYawVel === 0 && this.edgePitchVel === 0) return;

        this.yaw += this.edgeYawVel * dt;
        this.pitch = Math.max(-PITCH_CLAMP, Math.min(PITCH_CLAMP, this.pitch + this.edgePitchVel * dt));
        // The taught gesture is happening — lets the practice card tick "look".
        if (Math.abs(this.edgeYawVel) + Math.abs(this.edgePitchVel) > 0.05) this.gestureLook = true;
    }

    private updateFloorTarget(): void {
        const cam = this.camera.camera;
        // No indicator while the view is grabbed — it would swim under a
        // rotating camera.
        if (!this.clickToMove || !this.mouseIn || this.dragId !== null || !this.canvas || !cam) {
            this.targetValid = false;
            if (this.indicator) this.indicator.enabled = false;
            return;
        }
        const rect = this.canvas.getBoundingClientRect();
        const sx = this.mouseX - rect.left;
        const sy = this.mouseY - rect.top;
        cam.screenToWorld(sx, sy, cam.nearClip, this._near);
        cam.screenToWorld(sx, sy, cam.farClip, this._far);
        this._dir.copy(this._far).sub(this._near).normalize();
        const ro: [number, number, number] = [this._near.x, this._near.y, this._near.z];
        const rd: [number, number, number] = [this._dir.x, this._dir.y, this._dir.z];

        // Nearest walkable surface under the cursor — any level or stair ramp
        // (top-side hits only, so aiming at an upper floor's underside from
        // below never targets it).
        const surfaceHit = raycastWalkSurfaces(ro, rd, this.surfaces);
        let tx: number, ty: number, tz: number;
        if (surfaceHit) {
            tx = surfaceHit.point[0]; ty = surfaceHit.point[1]; tz = surfaceHit.point[2];
        } else {
            // Cursor is off every walkable footprint: a wall, a painting, the
            // ceiling. "Click the thing to get close": walk along the line of
            // sight until the floor runs out, and stop a step short.
            const ground = this.surfaces[0];
            const toward = ground ? this.marchToward(rd, ground.poly) : null;
            if (!ground || !toward) {
                this.targetValid = false;
                if (this.indicator) this.indicator.enabled = false;
                return;
            }
            tx = toward.x; tz = toward.z;
            ty = planeHeightAt(ground.plane, tx, tz);
        }
        // With collision voxels the disc lies on whatever the cursor is over —
        // floor, wall or object — facing out of it (Matterport style). The
        // move target stays on the walk surfaces: an object between the
        // cursor and the floor point sends you to the floor just in front of
        // it, where a click would stop anyway.
        let mark: { p: number[]; n: number[] } | null = null;
        if (this.voxels) {
            const vox = this.voxels;
            // the ray march is the cost here: redo it only when the ray moved
            const ray = this._markRay;
            if (ray.vox !== vox || ro.some((v, i) => v !== ray.ro[i]) || rd.some((v, i) => v !== ray.rd[i])) {
                ray.vox = vox; ray.ro = ro; ray.rd = rd;
                ray.t = vox.raycast(ro, rd, OBJECT_PICK_RANGE);
            }
            const t = ray.t;
            const floorDist = Math.hypot(tx - ro[0], ty - ro[1], tz - ro[2]);
            if (!Number.isNaN(t)) {
                const p = [ro[0] + rd[0] * t, ro[1] + rd[1] * t, ro[2] + rd[2] * t];
                const n = vox.normalAt(p[0], p[1], p[2]);
                // floors read flat, whatever the grid's stair-stepping says:
                // near-vertical normals, and anything sitting on a walk surface
                // (level floors only: a stair ramp runs between the steps, so
                // there the disc stays on the step the voxels found)
                const floor = raycastWalkSurfaces([p[0], p[1] + FLOOR_SNAP, p[2]], [0, -1, 0], this.surfaces);
                const onFloor = !!floor && this.surfaces[floor.surface]?.kind === 'level' &&
                    Math.abs(p[1] - floor.point[1]) < FLOOR_SNAP;
                if (onFloor || n[1] > FLOOR_NORMAL_Y) { n[0] = 0; n[1] = 1; n[2] = 0; }
                // The disc's own copy: `p` below decides where a click walks
                // to and must stay where the ray met the voxels.
                if (n[0] || n[1] || n[2]) mark = { p: onFloor ? [p[0], floor!.point[1], p[2]] : p, n };
                if (t < floorDist) {
                    // an object: the floor in front of it; the floor itself
                    // (a plane drawn a little under the real surface): where
                    // the ray meets the voxels, at the plane's height
                    const front = this.frontOfObject(p, rd);
                    const under = front ? null : raycastWalkSurfaces([p[0], p[1] + 0.5, p[2]], [0, -1, 0], this.surfaces);
                    if (front) { tx = front[0]; ty = front[1]; tz = front[2]; } else if (under) { tx = p[0]; ty = under.point[1]; tz = p[2]; }
                }
            }
        }
        this.targetX = tx; this.targetZ = tz; this.targetY = ty; this.targetValid = true;
        if (this.indicator) {
            if (mark) {
                const [nx, ny, nz] = mark.n;
                this.indicator.setPosition(mark.p[0] + nx * 0.02, mark.p[1] + ny * 0.02, mark.p[2] + nz * 0.02);
                // the disc's flat side (local +Y) turned onto the surface direction
                const ax = nz, az = -nx;                 // up × n
                const al = Math.hypot(ax, az);
                if (al < 1e-6) {
                    this.indicator.setEulerAngles(ny < 0 ? 180 : 0, 0, 0);
                } else {
                    this._markAxis.set(ax / al, 0, az / al);
                    this._markRot.setFromAxisAngle(this._markAxis, Math.acos(Math.max(-1, Math.min(1, ny))) * RAD2DEG);
                    this.indicator.setRotation(this._markRot);
                }
            } else {
                this.indicator.setPosition(tx, ty + 0.02, tz);
                this.indicator.setEulerAngles(0, 0, 0);
            }
            this.indicator.enabled = true;
            // A slow breathe, so the ring reads as "you can go here".
            this.indicatorT += 1 / 60;
            const sc = 1 + 0.08 * Math.sin(this.indicatorT * 4);
            this.indicator.setLocalScale(sc, 1, sc);
        }
    }

    /**
     * The cursor ray hit voxels at `hit` before reaching the floor point: if
     * that is an object (clearly above the walk surface there), the walk-surface
     * point just in front of it, on the camera's side. Null when it is the
     * floor itself.
     */
    private frontOfObject(hit: number[], rd: [number, number, number]): [number, number, number] | null {
        const vox = this.voxels!;
        const [hx, hy, hz] = hit;
        const under = raycastWalkSurfaces([hx, hy + 0.01, hz], [0, -1, 0], this.surfaces);
        // the floor's own voxels (and anything stepped over) are not an object
        if (!under || hy - under.point[1] < vox.stepHeight) return null;
        const hl = Math.hypot(rd[0], rd[2]) || 1;
        const bx = hx - rd[0] / hl * (vox.radius + OBJECT_STANDOFF);
        const bz = hz - rd[2] / hl * (vox.radius + OBJECT_STANDOFF);
        const floor = raycastWalkSurfaces([bx, hy + 0.01, bz], [0, -1, 0], this.surfaces);
        return floor ? floor.point : null;
    }

    /**
     * From the camera, walk the ray's horizontal direction across the ground
     * footprint and return the last inside point minus WALL_STOP_M. Null when
     * the ray is near-vertical or the camera is already up close.
     */
    private marchToward(rd: [number, number, number], poly: [number, number][]): { x: number; z: number } | null {
        const hl = Math.hypot(rd[0], rd[2]);
        if (hl < 0.15) return null;   // straight up / down: no "toward"
        const dx = rd[0] / hl, dz = rd[2] / hl;
        const p = this.camera.getPosition();
        let last = 0;
        for (let d = WALL_MARCH_STEP; d <= WALL_MARCH_MAX; d += WALL_MARCH_STEP) {
            if (!pointInPolygon(p.x + dx * d, p.z + dz * d, poly)) break;
            last = d;
        }
        const stop = last - WALL_STOP_M;
        if (stop < 0.3) return null;
        return { x: p.x + dx * stop, z: p.z + dz * stop };
    }

    /** Push the camera toward screen point (x, y) by `amount` (negative = back). */
    private pinchToward(x: number, y: number, amount: number): void {
        const cam = this.camera.camera;
        if (!cam || !this.canvas || amount === 0) return;
        const rect = this.canvas.getBoundingClientRect();
        cam.screenToWorld(x - rect.left, y - rect.top, cam.nearClip, this._near);
        cam.screenToWorld(x - rect.left, y - rect.top, cam.farClip, this._far);
        this._dir.copy(this._far).sub(this._near);
        let fx = this._dir.x, fz = this._dir.z;
        if (Math.hypot(fx, fz) < 1e-6) {
            // Looking straight down: fall back to the view's forward.
            fx = this.camera.forward.x; fz = this.camera.forward.z;
        }
        const l = Math.hypot(fx, fz) || 1;
        this.pinchVX += (fx / l) * amount;
        this.pinchVZ += (fz / l) * amount;
        this.sliding = false;
    }

    private touchPair(): { cx: number; cy: number; dist: number } {
        const pts = [...this.touches.values()];
        const a = pts[0], b = pts[1] ?? pts[0];
        return { cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, dist: Math.hypot(a.x - b.x, a.y - b.y) };
    }

    private beginTwoFingerNav(): void {
        const t = this.touchPair();
        this.tfMode = 'undecided';
        this.tfStartCX = this.tfPrevCX = t.cx;
        this.tfStartCY = this.tfPrevCY = t.cy;
        this.tfStartDist = this.tfPrevDist = t.dist;
    }

    /**
     * Two fingers on glass do one of two things, decided in the first
     * TWO_FINGER_DECIDE_PX and then locked for the gesture: the gap changing
     * more than the midpoint moves = a pinch (walk toward the midpoint),
     * otherwise a pan (up = forward, sideways = step aside, like a trackpad).
     */
    private updateTwoFingerNav(): void {
        const t = this.touchPair();
        if (this.tfMode === 'undecided') {
            const spread = Math.abs(t.dist - this.tfStartDist);
            const travel = Math.hypot(t.cx - this.tfStartCX, t.cy - this.tfStartCY);
            if (spread > TWO_FINGER_DECIDE_PX || travel > TWO_FINGER_DECIDE_PX) {
                this.tfMode = spread > travel ? 'pinch' : 'pan';
            }
        }
        if (this.tfMode === 'pinch') {
            this.pinchToward(t.cx, t.cy, (t.dist - this.tfPrevDist) * TOUCH_PINCH_SENS);
            this.gesturePinch = true;
            this.gestureTwoFinger = true;
        } else if (this.tfMode === 'pan') {
            this.scrollVel += -(t.cy - this.tfPrevCY) * TOUCH_PAN_SENS;
            this.strafeVel += (t.cx - this.tfPrevCX) * TOUCH_PAN_SENS;
            this.sliding = false;
            this.gestureTwoFinger = true;
        }
        this.tfPrevCX = t.cx; this.tfPrevCY = t.cy; this.tfPrevDist = t.dist;
    }

    private createIndicator(): void {
        // A terracotta disc with a white centre: the "go here" mark the tour
        // teaches. Big enough to read across a room (radius ~0.22 m).
        const overlay = this.app.scene.layers.getLayerByName('Immediate');
        const disc = (name: string, color: pc.Color, opacity: number, r: number, lift: number) => {
            const mat = new pc.StandardMaterial();
            mat.useLighting = false;
            mat.emissive = color;
            mat.opacity = opacity;
            mat.blendType = pc.BLEND_NORMAL;
            mat.depthWrite = false;
            // it sits on the cursor's first surface: nothing should hide it,
            // and the splat surface often lies a few cm in front of the walk
            // plane or the voxels it is placed on
            mat.depthTest = false;
            mat.update();
            const e = new pc.Entity(name);
            // drawn after the splats: in the World layer they blend over it
            e.addComponent('render', { type: 'cylinder', material: mat, ...(overlay ? { layers: [overlay.id] } : {}) });
            // unit cylinder is radius 0.5, height 1.
            e.setLocalScale(r * 2, 0.002, r * 2);
            e.setLocalPosition(0, lift, 0);
            return e;
        };
        const root = new pc.Entity('walk-indicator');
        root.addChild(disc('walk-indicator-ring', RING_COLOR, 0.8, 0.22, 0));
        root.addChild(disc('walk-indicator-dot', new pc.Color(1, 1, 1), 0.9, 0.08, 0.003));
        root.enabled = false;
        this.app.root.addChild(root);
        this.indicator = root;
    }

    // ── Virtual joystick (mouse + touch) ─────────────────────────────────────
    // A self-contained on-screen pad, bottom-right, like Spark's nipplejs: drag
    // the knob → a normalised movement vector (x = strafe, y = forward) that
    // update() folds into the same target velocity as WASD. No external dep.

    /** Where both pads are mounted: the canvas's parent — the same element the
     *  toolbar asks to go fullscreen. It has to be inside it: a fullscreen
     *  element's subtree is the ONLY thing the browser paints, so the pads used
     *  to vanish the moment fullscreen was entered (they hung off document.body,
     *  a sibling of that element). Both stay position:fixed, so nothing about
     *  where they sit changes outside fullscreen. */
    private padHost(): HTMLElement {
        return this.canvas?.parentElement ?? document.body;
    }

    private createJoystick(): void {
        if (this.joyEl || !this.joystickAllowed || this.joystickMode !== 'fixed' || typeof document === 'undefined') return;
        if (hasFinePointer()) return;

        const base = document.createElement('div');
        // Named so the stand demo can keep it out of sight while its loader
        // is up (the pad is a sibling of the ui root and paints above it).
        base.className = 'ollo-joystick';
        base.style.cssText =
            'position:fixed;right:28px;bottom:calc(28px + env(safe-area-inset-bottom, 0px));width:110px;height:110px;' +
            'border-radius:50%;background:rgba(255,255,255,0.10);' +
            'border:2px solid rgba(255,255,255,0.45);box-shadow:0 2px 12px rgba(0,0,0,0.35);' +
            'touch-action:none;z-index:20;pointer-events:auto;display:flex;' +
            'align-items:center;justify-content:center;cursor:grab;' +
            'backdrop-filter:blur(2px);-webkit-backdrop-filter:blur(2px);';

        const knob = document.createElement('div');
        knob.style.cssText =
            'width:50px;height:50px;border-radius:50%;background:rgba(255,255,255,0.85);' +
            'box-shadow:0 1px 6px rgba(0,0,0,0.4);transform:translate(0px,0px);pointer-events:none;';
        base.appendChild(knob);
        // Same long-press guard as the canvas (mount.ts): a thumb resting on
        // the pad must not raise iOS's selection or callout.
        base.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

        const max = this.joyMaxTravel;
        const setFrom = (clientX: number, clientY: number) => {
            const r = base.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            let dx = clientX - cx;
            let dy = clientY - cy;
            const len = Math.hypot(dx, dy);
            if (len > max) { dx = (dx / len) * max; dy = (dy / len) * max; }
            knob.style.transform = `translate(${dx}px, ${dy}px)`;
            this.joystickX = dx / max;        // +right
            this.joystickY = -dy / max;       // +forward (screen-y points down)
            // Deliberate deflection (not a stray tap on the pad) counts as
            // the taught "joystick to move" gesture.
            if (Math.hypot(this.joystickX, this.joystickY) > 0.2) this.gestureJoystick = true;
        };

        const onJoyDown = (e: PointerEvent) => {
            e.preventDefault(); e.stopPropagation();
            this.joyPointerId = e.pointerId;
            try { base.setPointerCapture(e.pointerId); } catch { /* ignore */ }
            knob.style.transition = 'none';
            this.sliding = false;             // joystick cancels click-to-move glide
            setFrom(e.clientX, e.clientY);
        };
        const onJoyMove = (e: PointerEvent) => {
            if (this.joyPointerId !== e.pointerId) return;
            e.preventDefault(); e.stopPropagation();
            setFrom(e.clientX, e.clientY);
        };
        const onJoyUp = (e: PointerEvent) => {
            if (this.joyPointerId !== e.pointerId) return;
            e.preventDefault(); e.stopPropagation();
            this.joyPointerId = null;
            try { base.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
            knob.style.transition = 'transform 0.12s ease-out';
            knob.style.transform = 'translate(0px, 0px)';
            this.joystickX = 0; this.joystickY = 0;
        };

        base.addEventListener('pointerdown', onJoyDown);
        base.addEventListener('pointermove', onJoyMove);
        base.addEventListener('pointerup', onJoyUp);
        base.addEventListener('pointercancel', onJoyUp);
        base.addEventListener('contextmenu', (e) => e.preventDefault());

        this.padHost().appendChild(base);
        this.joyEl = base;
        this.joyKnob = knob;
    }

    private destroyJoystick(): void {
        this.joystickX = 0;
        this.joystickY = 0;
        this.joyPointerId = null;
        if (this.joyEl) { this.joyEl.remove(); this.joyEl = null; this.joyKnob = null; }
    }

    /**
     * End every joystick interaction and zero the movement vector. The pads
     * normally hear their own pointerup via pointer capture, but capture is
     * not guaranteed — it can fail to take, or be lost mid-drag — and then
     * the release lands on whatever sits under the cursor while the stick
     * stays deflected and the camera walks forever. Called by the window-level
     * safety net in attach() on any pointer release/cancel or focus loss.
     */
    private resetSticks(): void {
        if (this.joyPointerId !== null) {
            this.joyPointerId = null;
            if (this.joyKnob) {
                this.joyKnob.style.transition = 'transform 0.12s ease-out';
                this.joyKnob.style.transform = 'translate(0px, 0px)';
            }
            this.joystickX = 0;
            this.joystickY = 0;
        }
        if (this.floatId !== null || this.floatEl) this.endFloatingJoystick();
    }

    // ── Floating joystick (mobile scheme E) ──────────────────────────────────
    // The same pad, drawn where the finger landed and gone when it lifts. The
    // canvas owns the pointer (captured in onDown), so onMove/onUp drive it.
    private startFloatingJoystick(id: number, x: number, y: number): void {
        this.floatId = id;
        this.floatCX = x;
        this.floatCY = y;
        this.sliding = false;
        const base = document.createElement('div');
        base.className = 'ollo-joystick';
        base.style.cssText =
            `position:fixed;left:${x - 55}px;top:${y - 55}px;width:110px;height:110px;` +
            'border-radius:50%;background:rgba(255,255,255,0.10);' +
            'border:2px solid rgba(255,255,255,0.45);box-shadow:0 2px 12px rgba(0,0,0,0.35);' +
            'z-index:20;pointer-events:none;display:flex;align-items:center;justify-content:center;' +
            'backdrop-filter:blur(2px);-webkit-backdrop-filter:blur(2px);';
        const knob = document.createElement('div');
        knob.style.cssText =
            'width:50px;height:50px;border-radius:50%;background:rgba(255,255,255,0.85);' +
            'box-shadow:0 1px 6px rgba(0,0,0,0.4);transform:translate(0px,0px);pointer-events:none;';
        base.appendChild(knob);
        this.padHost().appendChild(base);
        this.floatEl = base;
        this.floatKnob = knob;
    }

    private updateFloatingJoystick(x: number, y: number): void {
        const max = this.joyMaxTravel;
        let dx = x - this.floatCX;
        let dy = y - this.floatCY;
        const len = Math.hypot(dx, dy);
        if (len > max) { dx = (dx / len) * max; dy = (dy / len) * max; }
        if (this.floatKnob) this.floatKnob.style.transform = `translate(${dx}px, ${dy}px)`;
        this.joystickX = dx / max;
        this.joystickY = -dy / max;
        if (Math.hypot(this.joystickX, this.joystickY) > 0.2) this.gestureJoystick = true;
    }

    private endFloatingJoystick(): void {
        if (this.floatId === null && !this.floatEl) return;
        this.floatId = null;
        this.joystickX = 0;
        this.joystickY = 0;
        if (this.floatEl) { this.floatEl.remove(); this.floatEl = null; this.floatKnob = null; }
    }

    private touchCentroidY(): number {
        let s = 0, n = 0;
        for (const t of this.touches.values()) { s += t.y; n++; }
        return n ? s / n : 0;
    }
}
